"use client";
import {
  FC,
  ReactNode,
  useEffect,
  useState,
  useMemo,
  useRef,
  createContext,
  useContext,
} from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { SearchResults, BannerProduct } from "./InternalSearchResults";
import { CategoryFacet } from "./SWDCInternalSearchLeftFilter";
import { getSearchResults } from "@/helpers/api";

import { useDebouncedCallback } from "@/hooks";

type SearchApiResponse = SearchResults & {
  categoryFacets?: CategoryFacet[];
  bannerProduct?: BannerProduct | null;
};

type SearchContextType = {
  searchResults: SearchResults | null;
  categories: CategoryFacet[];
  isLoading: boolean;
  bannerProduct: BannerProduct | null;
  searchQuery: string;
};

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearchResults = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    return {
      searchResults: null,
      categories: [],
      isLoading: false,
      bannerProduct: null,
      searchQuery: "",
    };
  }
  return context;
};

type SWDCInternalSearchWrapperProps = {
  children?: ReactNode;
  searchValue?: string;
};

export const SWDCInternalSearchWrapper: FC<SWDCInternalSearchWrapperProps> = ({
  children,
  searchValue,
}) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const rawSearchQuery = searchValue || searchParams?.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(rawSearchQuery);

  const debouncedSetSearchQuery = useDebouncedCallback(
    (value: string) => setSearchQuery(value),
    300,
  );

  useEffect(() => {
    debouncedSetSearchQuery(rawSearchQuery);
  }, [rawSearchQuery, debouncedSetSearchQuery]);

  const [searchResults, setSearchResults] = useState<SearchResults | null>(
    null,
  );
  const [categories, setCategories] = useState<CategoryFacet[]>([]);
  const [bannerProduct, setBannerProduct] = useState<BannerProduct | null>(
    null,
  );
  // Initialize loading based on whether there's a query
  // Use a function initializer to only run once on mount
  const [isLoading, setIsLoading] = useState(() => {
    const hasInitialQuery =
      searchValue?.trim() || searchParams?.get("q")?.trim();
    return !!hasInitialQuery;
  });

  const latestRequestId = useRef(0);

  const filterGroup = useMemo(() => {
    const pathSegments = pathname?.split("/").filter(Boolean) || [];
    const lastSegment = pathSegments[pathSegments.length - 1];
    if (
      lastSegment &&
      ["resources", "government", "everything"].includes(lastSegment)
    ) {
      return lastSegment;
    }
    return "products";
  }, [pathname]);

  const categoryFilter = searchParams?.get("c") || "";
  const pageNumber = parseInt(searchParams?.get("page") || "1", 10);

  const clearSearchState = (ignoreLateResponse = false) => {
    if (ignoreLateResponse) {
      latestRequestId.current += 1;
    }
    setSearchResults(null);
    setCategories([]);
    setBannerProduct(null);
    setIsLoading(false);
  };

  // Clear state when there's no search query
  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === "") {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clearing state when query is removed is intentional
      clearSearchState(true);
    }
  }, [searchQuery]);

  // Fetch search results when there's a query
  useEffect(() => {
    const hasQuery = searchQuery && searchQuery.trim() !== "";
    if (!hasQuery) return;

    const requestId = ++latestRequestId.current;

    // Set loading immediately when we have a query
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    // Immediately set loading state to true for user feedback
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Setting loading synchronously is required for immediate feedback when search happens before page load
    setIsLoading(true);

    // Safety net: Force loading to false after 60 seconds (WIA API can be slow)
    timeoutId = setTimeout(() => {
      // Only act if this request is still the latest one
      if (requestId === latestRequestId.current) {
        console.error(`Search request timed out for query: "${searchQuery}"`);
        clearSearchState(true);
      }
    }, 60000);

    const clearTimeoutIfExists = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const fetchSearchData = async () => {
      const payload = {
        searchTerm: searchQuery,
        filterGroup: filterGroup.charAt(0).toUpperCase() + filterGroup.slice(1),
        categoryFilter,
        pageNumber: String(pageNumber),
        siteFilter: "swdc",
      };

      try {
        const data = await getSearchResults<SearchApiResponse>(payload);

        // Ignore if a newer request has since started
        if (requestId !== latestRequestId.current) return;

        setSearchResults(data);
        setBannerProduct(data.bannerProduct || null);

        if (data.categoryFacets) {
          const sortedCategories = [...data.categoryFacets].sort(
            (a: CategoryFacet, b: CategoryFacet) =>
              a.calculationPriority - b.calculationPriority,
          );
          setCategories(sortedCategories);
        } else {
          setCategories([]);
        }

        setIsLoading(false);
        clearTimeoutIfExists();
      } catch {
        if (requestId !== latestRequestId.current) return;

        clearSearchState();
        clearTimeoutIfExists();
      }
    };

    fetchSearchData();

    return () => {
      // already prevents this effect's stale result from being applied.
      clearTimeoutIfExists();
    };
  }, [searchQuery, filterGroup, categoryFilter, pageNumber]);

  return (
    <SearchContext.Provider
      value={{
        searchResults,
        categories,
        isLoading,
        bannerProduct,
        searchQuery,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
